// client/src/pages/csgo/Items.js

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Spinner, Card, Table, Badge, Form, Row, Col, Button } from 'react-bootstrap';
import { FaCheck, FaTag } from 'react-icons/fa';
import useCsgo from '../../hooks/useCsgo';

// Tout l'espace CS:GO est en USD (BigQuery et CSFloat sont nativement en dollars).
const usd = (v) =>
  v == null
    ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v));

const STATUS = {
  IN_INVENTORY: { label: 'En inventaire', bg: 'primary' },
  SOLD: { label: 'Vendu', bg: 'success' },
  GONE: { label: 'Sorti', bg: 'warning', text: 'dark' }
};

const DISPLAY_LIMIT = 2000;

// Tri sur une date optionnelle : les items sans date coulent en bas.
const dateVal = (d) => (d ? new Date(d).getTime() : -Infinity);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

function Items() {
  const { items, loading, error, fetchItems, updateItem } = useCsgo();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [sortKey, setSortKey] = useState('bought');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (statusFilter !== 'ALL' && i.status !== statusFilter) return false;
      if (typeFilter === 'SNIPE' && !i.isSnipe) return false;
      if (typeFilter === 'NOTSNIPE' && i.isSnipe) return false;
      if (q && !i.marketHashName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, statusFilter, typeFilter]);

  const sorted = useMemo(() => {
    const val = (i) => {
      switch (sortKey) {
        case 'name':
          return i.marketHashName.toLowerCase();
        case 'bought':
          return dateVal(i.boughtAt);
        case 'sold':
          return dateVal(i.soldAt);
        case 'buy':
          return i.buyPriceUsd == null ? -Infinity : Number(i.buyPriceUsd);
        case 'sell':
          return i.sellNetUsd == null ? -Infinity : Number(i.sellNetUsd);
        case 'value':
          return i.currentValueUsd == null ? -Infinity : Number(i.currentValueUsd);
        case 'profit':
          return i.profitUsd == null ? -Infinity : Number(i.profitUsd);
        default:
          return 0;
      }
    };
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const shown = sorted.slice(0, DISPLAY_LIMIT);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const sortIndicator = (k) => (sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const sortableTh = (key, label, className = '') => (
    <th
      className={`pb-2 ${className}`}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => toggleSort(key)}
    >
      {label}
      {sortIndicator(key)}
    </th>
  );

  // Vente manuelle (item sorti hors CSFloat, ou correction) : prix BRUT en USD.
  const handleMarkSold = async (item) => {
    const raw = window.prompt(
      `Prix de vente BRUT (USD) pour ${item.marketHashName} :\n(vendu hors CSFloat — laisser vide pour annuler)`,
      ''
    );
    if (raw == null || raw.trim() === '') return;
    const price = parseFloat(raw.replace(',', '.'));
    if (Number.isNaN(price)) return;
    await updateItem(item.id, {
      status: 'SOLD',
      sellPrice: price,
      soldChannel: 'other',
      soldAt: new Date().toISOString()
    });
  };

  // Prix d'achat manuel (items non-snipe : play skins, achats hors sniper).
  const handleSetBuyPrice = async (item) => {
    const raw = window.prompt(`Prix d'achat (USD) pour ${item.marketHashName} :`, '');
    if (raw == null || raw.trim() === '') return;
    const price = parseFloat(raw.replace(',', '.'));
    if (Number.isNaN(price)) return;
    await updateItem(item.id, { buyPrice: price });
  };

  return (
    <div className="animate-fade-in pb-5">
      <h1 className="h4 mb-4 fw-bold">Items</h1>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="border-0 shadow-sm bg-white">
        <Card.Body className="p-4">
          <Row className="g-2 mb-3">
            <Col md={5}>
              <Form.Control
                size="sm"
                placeholder="Rechercher un skin..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Form.Select
                size="sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">Tous les statuts</option>
                <option value="IN_INVENTORY">En inventaire</option>
                <option value="SOLD">Vendus</option>
                <option value="GONE">Sortis (hors CSFloat)</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="ALL">Snipes + autres</option>
                <option value="SNIPE">Snipes uniquement</option>
                <option value="NOTSNIPE">Hors snipe (play/LT)</option>
              </Form.Select>
            </Col>
          </Row>

          {loading.items && !items.length ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : (
            <>
              <div className="text-muted small mb-2">
                {filtered.length} item(s)
                {filtered.length > DISPLAY_LIMIT && ` — ${DISPLAY_LIMIT} affichés`}
                <span className="ms-2">
                  · Les <strong>snipes</strong> sont financés par le partenaire : « Ma part » =
                  vente nette − (remboursement de l'achat + 50 % du profit brut). Les frais de vente
                  (2 %) restent à ma charge ; à perte, l'achat est remboursé à 100 %.
                </span>
              </div>
              <div className="table-responsive">
                <Table borderless size="sm" className="align-middle mb-0">
                  <thead className="text-muted text-xs text-uppercase border-bottom">
                    <tr>
                      {sortableTh('name', 'Skin')}
                      <th className="pb-2 text-end">Float</th>
                      <th className="pb-2 text-center">Statut</th>
                      {sortableTh('bought', 'Acheté le')}
                      {sortableTh('buy', 'Prix achat', 'text-end')}
                      {sortableTh('sold', 'Vendu le')}
                      {sortableTh('sell', 'Prix vente', 'text-end')}
                      {sortableTh('value', 'Valeur actuelle', 'text-end')}
                      {sortableTh('profit', 'Ma part', 'text-end')}
                      <th className="pb-2 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((i) => {
                      const st = STATUS[i.status] || { label: i.status, bg: 'secondary' };
                      const isSold = i.status === 'SOLD';
                      const profitTone = Number(i.profitUsd) >= 0 ? 'text-success' : 'text-danger';
                      return (
                        <tr key={i.id}>
                          <td className="py-2 fw-semibold text-sm">
                            {i.marketHashName}
                            {i.isSnipe && (
                              <Badge bg="info" className="ms-2 text-xs">
                                Snipe
                              </Badge>
                            )}
                          </td>
                          <td
                            className="py-2 text-end text-xs text-muted"
                            title={i.floatValue != null ? String(i.floatValue) : undefined}
                          >
                            {i.floatValue != null ? Number(i.floatValue).toFixed(6) : '—'}
                          </td>
                          <td className="py-2 text-center">
                            <Badge bg={st.bg} text={st.text}>
                              {st.label}
                            </Badge>
                            {isSold && i.soldChannel === 'other' && (
                              <div className="text-xs text-muted">hors CSFloat</div>
                            )}
                          </td>
                          <td className="py-2 text-xs text-muted">{fmtDate(i.boughtAt)}</td>
                          <td className="py-2 text-end text-sm">
                            {usd(i.buyPriceUsd)}
                            {i.buyPriceSource && (
                              <div className="text-xs text-muted">
                                via{' '}
                                {i.buyPriceSource === 'bigquery'
                                  ? 'sniper'
                                  : i.buyPriceSource === 'manual'
                                    ? 'saisie manuelle'
                                    : i.buyPriceSource}
                              </div>
                            )}
                          </td>
                          <td className="py-2 text-xs text-muted">{fmtDate(i.soldAt)}</td>
                          <td className="py-2 text-end text-sm fw-bold">
                            {isSold ? usd(i.sellNetUsd) : <span className="text-muted">—</span>}
                          </td>
                          <td className="py-2 text-end text-sm">
                            {i.status === 'IN_INVENTORY' ? (
                              usd(i.currentValueUsd)
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td
                            className={`py-2 text-end text-sm fw-bold ${profitTone}`}
                            title={
                              i.isSnipe
                                ? 'Ma part = vente nette − (remboursement achat + 50 % du profit brut au partenaire)'
                                : isSold
                                  ? 'Profit réalisé (net de frais de vente)'
                                  : 'Plus-value latente (net de frais de vente)'
                            }
                          >
                            {i.profitUsd == null
                              ? '—'
                              : `${Number(i.profitUsd) >= 0 ? '+' : ''}${usd(i.profitUsd)}`}
                            {i.profitPct != null && (
                              <span className="text-muted fw-normal ms-1">
                                ({Number(i.profitPct) >= 0 ? '+' : ''}
                                {Number(i.profitPct).toFixed(0)}%)
                              </span>
                            )}
                            {i.partnerDueUsd != null && (
                              <div
                                className="text-xs text-muted fw-normal"
                                title="Remboursement du prix d'achat + 50 % du profit brut"
                              >
                                dû partenaire : {usd(i.partnerDueUsd)}
                              </div>
                            )}
                          </td>
                          <td className="py-2 text-end">
                            {i.status === 'GONE' && (
                              <Button
                                variant="link"
                                size="sm"
                                className="text-success p-0 me-2"
                                title="Marquer vendu (hors CSFloat)"
                                onClick={() => handleMarkSold(i)}
                              >
                                <FaCheck />
                              </Button>
                            )}
                            {i.buyPriceUsd == null && (
                              <Button
                                variant="link"
                                size="sm"
                                className="text-primary p-0"
                                title="Renseigner le prix d'achat"
                                onClick={() => handleSetBuyPrice(i)}
                              >
                                <FaTag />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!shown.length && (
                      <tr>
                        <td colSpan={10} className="text-center text-muted py-4">
                          Aucun item. Commence par importer ton inventaire Steam (onglet Imports).
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default Items;
